import type { SocialShareType } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useSocialShareCount } from "~/components/article/article-share-widget/hooks";
import type { articleShareWidgetArticle$key } from "~/queries/__generated__/articleShareWidgetArticle.graphql";

import { Container, SocialShareCount, StyledContentShareWidget } from "./styles";

type Variants = "default" | "morningStudio" | "morningStudioSubheader" | "newsletter";

type OptionsMap = Record<
  Variants,
  {
    $iconBackgroundColor?: string;
    $iconColor?: string;
    isColumn: boolean;
    showCount: boolean;
    types: SocialShareType[];
  }
>;

type Props = {
  className?: string;
  reference: articleShareWidgetArticle$key;
  variant?: Variants;
};

export const ArticleShareWidget: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "default",
}) => {
  const content = useFragment<articleShareWidgetArticle$key>(
    graphql`
      fragment articleShareWidgetArticle on Article {
        ...contentShareWidgetContent
        ...articleCommentsTriggerArticle
        urlAlias
      }
    `,
    reference_,
  );

  const optionsMap = {
    default: {
      isColumn: true,
      showCount: true,
      types: ["facebook", "twitter", "whatsapp", "telegram", "copy-link", "email", "print"],
    },
    morningStudio: {
      isColumn: false,
      showCount: false,
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    morningStudioSubheader: {
      $iconBackgroundColor: "#909090",
      $iconColor: "#000000",
      isColumn: false,
      showCount: false,
      types: ["facebook", "twitter", "copy-link", "email"],
    },
    newsletter: {
      isColumn: false,
      showCount: false,
      types: ["facebook", "twitter", "copy-link", "email"],
    },
  } as OptionsMap;

  const socialShareCount = useSocialShareCount(content.urlAlias);
  const formatCount = (value?: number) =>
    value
      ? new Intl.NumberFormat("en-GB", {
          compactDisplay: "short",
          notation: "compact",
        })
          .format(value)
          .toLowerCase()
      : null;

  return (
    <Container className={className}>
      {optionsMap[variant].showCount && socialShareCount > 0 && (
        <SocialShareCount>{formatCount(socialShareCount)}</SocialShareCount>
      )}
      <StyledContentShareWidget
        $isColumn={optionsMap[variant].isColumn}
        extraProps={{
          copyLink: {
            variant: "tooltip",
          },
        }}
        reference={content}
        style={{
          backgroundColor: optionsMap[variant].$iconBackgroundColor,
          color: optionsMap[variant].$iconColor,
        }}
        types={optionsMap[variant].types}
      />
    </Container>
  );
};

ArticleShareWidget.displayName = "ArticleShareWidget";
